import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { trades as tradesApi, tradeNotes as notesApi } from '../api/client';
import TagManager from '../components/TagManager';
import ScreenshotGallery from '../components/ScreenshotGallery';
import MiniLineChart from '../components/MiniLineChart';

function formatPrice(p) {
  if (p == null) return '—';
  return parseFloat(p).toFixed(5);
}

function formatDate(ts) {
  if (!ts) return '—';
  const d = new Date(ts);
  return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function formatDuration(entry, exit) {
  if (!entry || !exit) return '—';
  const ms = new Date(exit) - new Date(entry);
  const mins = Math.floor(ms / 60000);
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  const remainMins = mins % 60;
  if (hours < 24) return `${hours}h ${remainMins}m`;
  const days = Math.floor(hours / 24);
  return `${days}d ${hours % 24}h`;
}

export default function TradeDetailPage() {
  const { sessionId, tradeId } = useParams();
  const [trade, setTrade] = useState(null);
  const [loading, setLoading] = useState(true);
  const [noteText, setNoteText] = useState('');

  const loadTrade = useCallback(() => {
    tradesApi.detail(sessionId, tradeId)
      .then((res) => setTrade(res.data))
      .catch((err) => console.error('Failed to load trade:', err))
      .finally(() => setLoading(false));
  }, [sessionId, tradeId]);

  useEffect(() => { loadTrade(); }, [loadTrade]);

  const handleAddNote = (e) => {
    e.preventDefault();
    if (!noteText.trim()) return;
    const formData = new FormData();
    formData.append('note_type', 'text');
    formData.append('content', noteText.trim());
    notesApi.create(sessionId, tradeId, formData)
      .then(() => { setNoteText(''); loadTrade(); })
      .catch((err) => console.error('Failed to add note:', err));
  };

  const handleDeleteNote = (noteId) => {
    if (!window.confirm('Delete this note?')) return;
    notesApi.delete(sessionId, tradeId, noteId)
      .then(() => loadTrade())
      .catch((err) => console.error('Failed to delete note:', err));
  };

  const handleTagsChange = (newTags) => {
    setTrade((prev) => ({ ...prev, tags: newTags }));
  };

  if (loading) return <div className="page">Loading trade details...</div>;
  if (!trade) return <div className="page"><div className="error-msg">Trade not found.</div></div>;

  const pnl = parseFloat(trade.pnl || 0);
  const mae = trade.mae != null ? parseFloat(trade.mae) : null;
  const mfe = trade.mfe != null ? parseFloat(trade.mfe) : null;
  const pnlClass = pnl > 0 ? 'pnl-positive' : pnl < 0 ? 'pnl-negative' : '';
  const exitEfficiency = mfe && mfe > 0 ? Math.round(pnl / mfe * 100) : null;
  const leftOnTable = mfe != null ? Math.round((mfe - pnl) * 100) / 100 : null;

  const textNotes = (trade.trade_notes || []).filter((n) => n.note_type === 'text');
  const screenshots = (trade.trade_notes || []).filter((n) => n.note_type === 'screenshot');
  const pnlCurve = trade.pnl_curve || [];

  return (
    <div className="page trade-detail-page">
      <Link to="/journal" className="trade-detail-back">&larr; Back to Journal</Link>

      {/* Trade header */}
      <div className="trade-detail-header card">
        <div className="trade-detail-title">
          <span className={`dir-badge dir-${trade.direction}`}>
            {trade.direction?.toUpperCase()}
          </span>
          <span className="badge">{trade.session_instrument}</span>
          <span className="badge">{trade.session_timeframe}</span>
          <span className="badge">{trade.order_type}</span>
          {trade.exit_reason && <span className="badge">{trade.exit_reason}</span>}
          {trade.playbook_name && <span className="badge" style={{ background: '#7c3aed' }}>{trade.playbook_name}</span>}
        </div>

        <div className="trade-detail-grid">
          <div className="trade-detail-stat">
            <span className="trade-detail-label">Entry</span>
            <span className="trade-detail-value">{formatPrice(trade.entry_price)}</span>
            <span className="trade-detail-sub">{formatDate(trade.entry_timestamp)}</span>
          </div>
          <div className="trade-detail-stat">
            <span className="trade-detail-label">Exit</span>
            <span className="trade-detail-value">{formatPrice(trade.exit_price)}</span>
            <span className="trade-detail-sub">{formatDate(trade.exit_timestamp)}</span>
          </div>
          <div className="trade-detail-stat">
            <span className="trade-detail-label">P&L</span>
            <span className={`trade-detail-value trade-detail-pnl ${pnlClass}`}>
              {pnl > 0 ? '+' : ''}{pnl.toFixed(2)}
            </span>
          </div>
          <div className="trade-detail-stat">
            <span className="trade-detail-label">Volume</span>
            <span className="trade-detail-value">{trade.volume}</span>
          </div>
          <div className="trade-detail-stat">
            <span className="trade-detail-label">SL</span>
            <span className="trade-detail-value">{formatPrice(trade.stop_loss)}</span>
          </div>
          <div className="trade-detail-stat">
            <span className="trade-detail-label">TP</span>
            <span className="trade-detail-value">{formatPrice(trade.take_profit)}</span>
          </div>
          <div className="trade-detail-stat">
            <span className="trade-detail-label">Duration</span>
            <span className="trade-detail-value">
              {formatDuration(trade.entry_timestamp, trade.exit_timestamp)}
            </span>
          </div>
        </div>

        {/* MAE/MFE + Exit Efficiency */}
        {(mae != null || mfe != null) && (
          <div className="trade-detail-grid" style={{ marginTop: '0.75rem', borderTop: '1px solid var(--border-color)', paddingTop: '0.75rem' }}>
            {mae != null && (
              <div className="trade-detail-stat">
                <span className="trade-detail-label">MAE (Max Adverse)</span>
                <span className="trade-detail-value pnl-negative">{mae.toFixed(2)}</span>
              </div>
            )}
            {mfe != null && (
              <div className="trade-detail-stat">
                <span className="trade-detail-label">MFE (Max Favorable)</span>
                <span className="trade-detail-value pnl-positive">+{mfe.toFixed(2)}</span>
              </div>
            )}
            {exitEfficiency != null && (
              <div className="trade-detail-stat">
                <span className="trade-detail-label">Exit Efficiency</span>
                <span className={`trade-detail-value ${exitEfficiency >= 50 ? 'pnl-positive' : 'pnl-negative'}`}>
                  {exitEfficiency}%
                </span>
              </div>
            )}
            {leftOnTable != null && leftOnTable > 0 && (
              <div className="trade-detail-stat">
                <span className="trade-detail-label">Left on Table</span>
                <span className="trade-detail-value" style={{ color: '#f59e0b' }}>{leftOnTable.toFixed(2)}</span>
              </div>
            )}
          </div>
        )}

        {trade.session_name && (
          <div className="trade-detail-session">
            Session: {trade.session_name || `#${trade.session}`}
          </div>
        )}
      </div>

      {/* Running P&L Chart */}
      {pnlCurve.length > 1 && (
        <div className="trade-detail-section">
          <h3>Running P&L</h3>
          <div className="card" style={{ padding: '0.75rem' }}>
            <MiniLineChart
              data={pnlCurve.map((p, i) => ({
                x: i, y: p.pnl,
                label: `Bar ${i + 1}`,
                date: p.time ? new Date(p.time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '',
              }))}
              color="#6366f1"
              height={200}
              yFormat={(v) => v.toFixed(2)}
            />
          </div>
        </div>
      )}

      {/* Tags */}
      <div className="trade-detail-section">
        <h3>Tags</h3>
        <TagManager sessionId={sessionId} tradeId={tradeId}
          currentTags={trade.tags || []} onTagsChange={handleTagsChange} />
      </div>

      {/* Screenshots */}
      <div className="trade-detail-section">
        <h3>Screenshots ({screenshots.length})</h3>
        <ScreenshotGallery screenshots={screenshots}
          sessionId={sessionId} tradeId={tradeId} onUpdate={loadTrade} />
      </div>

      {/* Notes */}
      <div className="trade-detail-section">
        <h3>Notes ({textNotes.length})</h3>
        <div className="trade-notes-list">
          {textNotes.map((note) => (
            <div key={note.id} className="trade-note-item">
              <p>{note.content}</p>
              <div className="trade-note-meta">
                <span>{formatDate(note.created_at)}</span>
                <button className="positions-action-btn positions-close-btn"
                  onClick={() => handleDeleteNote(note.id)}>Delete</button>
              </div>
            </div>
          ))}
        </div>
        <form className="trade-note-form" onSubmit={handleAddNote}>
          <textarea value={noteText} onChange={(e) => setNoteText(e.target.value)}
            placeholder="Add a note..." rows={3} />
          <button type="submit" className="btn btn-sm" disabled={!noteText.trim()}>Add Note</button>
        </form>
      </div>
    </div>
  );
}
